import type { ReactNode} from "react";
import { useCallback, useMemo, useRef } from "react";
import type { CSSTransitionClassNames } from "react-transition-group/CSSTransition";
import type {
  EnterHandler,
  ExitHandler,
} from "react-transition-group/Transition";

import type {
  RefTransitionProps,
  TransitionCallbackProps,
} from "./transition.base";
import { RefTransition } from "./transition.base";

/**
 * most of the typings for our custom transition component has been pulled
 * directly from the react-transition-group/Transition component.
 *
 * However, only the required props (those that we are interested in) have been
 * extracted as well as custom child render method
 */
export type CssTransitionProps = RefTransitionProps & {
  /**
   * The animation `classNames` applied to the component as it enters or
   * exits. A single name can be provided and it will be suffixed for each
   * stage: e.g.
   *
   * `classNames="fade"` applies `fade-enter`, `fade-enter-active`,
   * `fade-exit`, `fade-exit-active`, `fade-appear`, and `fade-appear-active`.
   *
   * Each individual classNames can also be specified independently like:
   *
   * ```js
   * classNames={{
   * appear: 'my-appear',
   * appearActive: 'my-appear-active',
   * appearDone: 'my-appear-done',
   * enter: 'my-enter',
   * enterActive: 'my-enter-active',
   * enterDone: 'my-enter-done',
   * exit: 'my-exit',
   * exitActive: 'my-exit-active',
   * exitDone: 'my-exit-done'
   * }}
   * ```
   */
  classNames?: string | CSSTransitionClassNames | undefined;

  /**
   * in case an entering element is set to exiting before its initial
   * transition has finsihed, it may cause jumps in the transition (due to
   * changes from -enter-active to -exit and -exit-active).
   *
   * in some cases it may be desirable to retain the -enter-* classNames even
   * while exiting, to ensure that the entire transition lifecycle remains
   * smooth. Setting this property to true achieves this.
   *
   * @default false
   */
  retainClassnamesOnExit?: boolean;

  /**
   * if configured, a delay will be added between the onEnter and onEntering +
   * onExit and onExiting.
   *
   * This is useful if children adjusts their heights shortly after mounting,
   * as this allows heights to settle before reading them.
   */
  delayMs?: number | { enter: number; exit: number };
};

/**
 * custom Transtion component for react-transition-group similar to the
 * CSSTransition, however with a slightly altered API to improve DX.
 *
 * After the introduction of ReactStrictMode react-transition-group transitions
 * needs to be provided with a nodeRef prop to not use findDOMNode() which may
 * have a negative impact on performance.
 *
 * However, manually passing refs may become cumbersome to provide. Thus, this
 * transition aims to easy the API required to make Transitions for
 * react-transition-groups by provided all props required for children as
 * arguments.
 */
export function CSSRefTransition({
  classNames,
  timeout,
  retainClassnamesOnExit,
  delayMs,
  ...transitionProps
}: CssTransitionProps & Partial<TransitionCallbackProps>): ReactNode {
  const internalRef = useRef<HTMLElement>(null);
  const nodeRef = transitionProps.nodeRef ?? internalRef;

  const resolvedDelayMs = useMemo(
    () => delayMs,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      typeof delayMs === "number"
        ? delayMs
        : `${delayMs?.enter}-${delayMs?.exit}`,
    ],
  );

  const onEnter: EnterHandler<HTMLElement> = useCallback(
    (isAppearing) => {
      nodeRef.current?.classList.remove(
        resolveClassName(classNames, "exit"),
        resolveClassName(classNames, "exit-active"),
        resolveClassName(classNames, "exit-running"),
        resolveClassName(classNames, "exit-done"),
        resolveClassName(classNames, "enter-active"),
        resolveClassName(classNames, "enter-running"),
        resolveClassName(classNames, "enter-done"),
        resolveClassName(classNames, "appear-active"),
        resolveClassName(classNames, "appear-running"),
        resolveClassName(classNames, "appear-done"),
        resolveClassName(classNames, isAppearing ? "enter" : "appear"),
      );

      transitionProps.onEnter?.(isAppearing);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeRef, classNames, transitionProps.onEnter],
  );

  const onEntering: EnterHandler<HTMLElement> = useCallback(
    (isAppearing) => {
      const callback = () => {
        // Ensure that the initial transition values are applied before adding
        // the -active classname
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        nodeRef.current?.offsetHeight;

        nodeRef.current?.classList.remove(
          resolveClassName(classNames, "exit"),
          resolveClassName(classNames, "exit-active"),
          resolveClassName(classNames, "exit-running"),
          resolveClassName(classNames, "exit-done"),
          resolveClassName(classNames, "enter-active"),
          resolveClassName(classNames, "enter-running"),
          resolveClassName(classNames, "enter-done"),
          resolveClassName(classNames, "appear-active"),
          resolveClassName(classNames, "appear-running"),
          resolveClassName(classNames, "appear-done"),
          resolveClassName(classNames, isAppearing ? "enter" : "appear"),
        );
        nodeRef.current?.classList.add(
          resolveClassName(classNames, isAppearing ? "appear" : "enter"),
          resolveClassName(
            classNames,
            isAppearing ? "appear-active" : "enter-active",
          ),
        );

        transitionProps.onEntering?.(isAppearing);

        nodeRef.current?.classList.add(
          resolveClassName(
            classNames,
            isAppearing ? "appear-running" : "enter-running",
          ),
        );
      };

      const enterDelayMs = getDurationValue(delayMs, "enter");

      if (enterDelayMs) {
        setTimeout(callback, enterDelayMs);
      } else {
        requestAnimationFrame(callback);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeRef, classNames, transitionProps.onEntering],
  );

  const onEntered: EnterHandler<HTMLElement> = useCallback(
    (isAppearing) => {
      nodeRef.current?.classList.remove(
        resolveClassName(classNames, "exit"),
        resolveClassName(classNames, "exit-active"),
        resolveClassName(classNames, "exit-running"),
        resolveClassName(classNames, "exit-done"),
        resolveClassName(classNames, "enter"),
        resolveClassName(classNames, "enter-active"),
        resolveClassName(classNames, "enter-running"),
        resolveClassName(classNames, "enter-done"),
        resolveClassName(classNames, "appear"),
        resolveClassName(classNames, "appear-active"),
        resolveClassName(classNames, "appear-running"),
        resolveClassName(classNames, "appear-done"),
      );

      nodeRef.current?.classList.add(
        resolveClassName(
          classNames,
          isAppearing ? "appear-done" : "enter-done",
        ),
      );

      transitionProps.onEntered?.(isAppearing);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeRef, classNames, transitionProps.onEntered],
  );

  const onExit: ExitHandler<HTMLElement> = useCallback(() => {
    if (nodeRef.current) {
      nodeRef.current.dataset["disablePolly"] = "true";
    }

    if (!retainClassnamesOnExit) {
      nodeRef.current?.classList.remove(
        resolveClassName(classNames, "exit-active"),
        resolveClassName(classNames, "exit-running"),
        resolveClassName(classNames, "exit-done"),
        resolveClassName(classNames, "enter"),
        resolveClassName(classNames, "enter-active"),
        resolveClassName(classNames, "enter-running"),
        resolveClassName(classNames, "enter-done"),
        resolveClassName(classNames, "appear"),
        resolveClassName(classNames, "appear-active"),
        resolveClassName(classNames, "appear-running"),
        resolveClassName(classNames, "appear-done"),
      );
    }

    nodeRef.current?.classList.add(resolveClassName(classNames, "exit"));
    transitionProps.onExit?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, classNames, transitionProps.onExit]);

  const onExiting: ExitHandler<HTMLElement> = useCallback(() => {
    const callback = () => {
      // Ensure that the initial transition values are applied before adding
      // the -active classname
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      nodeRef.current?.offsetHeight;

      if (!retainClassnamesOnExit) {
        nodeRef.current?.classList.remove(
          resolveClassName(classNames, "exit-running"),
          resolveClassName(classNames, "exit-done"),
          resolveClassName(classNames, "enter"),
          resolveClassName(classNames, "enter-active"),
          resolveClassName(classNames, "enter-running"),
          resolveClassName(classNames, "enter-done"),
          resolveClassName(classNames, "appear"),
          resolveClassName(classNames, "appear-active"),
          resolveClassName(classNames, "appear-running"),
          resolveClassName(classNames, "appear-done"),
        );
      }

      nodeRef.current?.classList.add(
        resolveClassName(classNames, "exit"),
        resolveClassName(classNames, "exit-active"),
      );

      transitionProps.onExiting?.();

      nodeRef.current?.classList.add(
        resolveClassName(classNames, "exit-running"),
      );
    };

    const exitDelayMs = getDurationValue(resolvedDelayMs, "exit");

    if (exitDelayMs) {
      setTimeout(callback, exitDelayMs);
    } else {
      requestAnimationFrame(callback);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, classNames, transitionProps.onExiting]);

  const onExited: ExitHandler<HTMLElement> = useCallback(() => {
    nodeRef.current?.classList.remove(
      resolveClassName(classNames, "exit"),
      resolveClassName(classNames, "exit-active"),
      resolveClassName(classNames, "exit-running"),
      resolveClassName(classNames, "enter"),
      resolveClassName(classNames, "enter-active"),
      resolveClassName(classNames, "enter-running"),
      resolveClassName(classNames, "enter-done"),
      resolveClassName(classNames, "appear"),
      resolveClassName(classNames, "appear-active"),
      resolveClassName(classNames, "appear-running"),
      resolveClassName(classNames, "appear-done"),
    );
    nodeRef.current?.classList.add(resolveClassName(classNames, "exit-done"));

    if (nodeRef.current) {
      delete nodeRef.current.dataset["disablePolly"];
    }

    transitionProps.onExited?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, classNames, transitionProps.onExited]);

  const resolvedTimeout = useMemo(
    () => ({
      appear:
        getDurationValue(timeout, "appear") +
        getDurationValue(resolvedDelayMs, "enter"),
      enter:
        getDurationValue(timeout, "enter") +
        getDurationValue(resolvedDelayMs, "enter"),
      exit:
        getDurationValue(timeout, "exit") +
        getDurationValue(resolvedDelayMs, "exit"),
    }),
    [resolvedDelayMs, timeout],
  );

  return (
    <RefTransition
      {...transitionProps}
      timeout={resolvedTimeout}
      onEnter={onEnter}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      onExiting={onExiting}
      onExited={onExited}
      nodeRef={nodeRef}
    >
      {transitionProps.children}
    </RefTransition>
  );
}

function resolveClassName(
  base:
    | string
    | (CSSTransitionClassNames & {
        appearRunning?: string;
        enterRunning?: string;
        exitRunning?: string;
      })
    | undefined,
  stage: `${"appear" | "enter" | "exit"}${
    | ""
    | "-active"
    | "-running"
    | "-done"}`,
): string {
  let resolvedBase = base;

  if (typeof base === "object") {
    switch (stage) {
      case "appear":
        resolvedBase = base.appear;
        break;

      case "appear-active":
        resolvedBase = base.appearActive;
        break;

      case "appear-running":
        resolvedBase = base.appearRunning;
        break;

      case "appear-done":
        resolvedBase = base.appearDone;
        break;

      case "enter":
        resolvedBase = base.enter;
        break;

      case "enter-active":
        resolvedBase = base.enterActive;
        break;

      case "enter-running":
        resolvedBase = base.enterRunning;
        break;

      case "enter-done":
        resolvedBase = base.enterDone;
        break;

      case "exit":
        resolvedBase = base.exit;
        break;

      case "exit-active":
        resolvedBase = base.exitActive;
        break;

      case "exit-running":
        resolvedBase = base.exitRunning;
        break;

      case "exit-done":
        resolvedBase = base.exitDone;
        break;
    }
  }

  if (resolvedBase) {
    return `${resolvedBase}-${stage}`;
  } else {
    return stage;
  }
}

export function getDurationValue<T extends Record<string, number | undefined>>(
  duration: number | undefined | T,
  key: keyof T,
): number {
  return typeof duration === "number" ? duration : (duration?.[key] ?? 0);
}
